import { PlusIcon, RepeatIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { Column } from "@/components/Column";
import { ConfirmationDialog } from "@/components/ConfirmationDialog";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

import { CopyDayRangesPopover } from "./CopyDayRangesPopover";
import { TimePicker } from "./TimePicker";
import { useGetUserSettings } from "@/hooks/queries/useGetUserSettings";
import { useUpdateSetUserSettings } from "@/hooks/updateSets/useUpdateSetUserSettings";
import {
  DAYS_OF_WEEK_ORDERED,
  DEFAULT_AVAILABILITY_RANGE,
} from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { screeningAvailability as availability } from "@/lib/screeningAvailability";
import {
  AvailabilityTimeRange,
  DayOfWeek,
  ScreeningAvailability,
} from "@/types";

export const ScreeningAvailabilitySheet = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) => {
  const { data: settings } = useGetUserSettings();
  const update = useUpdateSetUserSettings();

  const persisted = availability.fromServer(
    settings?.screeningAvailability ?? null,
  );

  // Draft re-seeded each time the sheet opens. Cancel/discard reverts.
  const [draft, setDraft] = useState<ScreeningAvailability>(persisted);
  const [discardPromptOpen, setDiscardPromptOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(persisted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(persisted);

  const setDayRanges = (day: DayOfWeek, ranges: AvailabilityTimeRange[]) => {
    setDraft((prev) => availability.setDayRanges(prev, day, ranges));
  };

  const addRange = (day: DayOfWeek) => {
    setDayRanges(day, [...draft[day], { ...DEFAULT_AVAILABILITY_RANGE }]);
  };

  const removeRange = (day: DayOfWeek, idx: number) => {
    setDayRanges(
      day,
      draft[day].filter((_, i) => i !== idx),
    );
  };

  const updateRangeField = (
    day: DayOfWeek,
    idx: number,
    field: "startTime" | "endTime",
    value: string,
  ) => {
    setDayRanges(
      day,
      draft[day].map((r, i) => (i === idx ? { ...r, [field]: value } : r)),
    );
  };

  const copyRangesTo = (sourceDay: DayOfWeek, targetDays: DayOfWeek[]) => {
    mixpanelUtils.trackButtonClick(
      "Copy screen availability to days",
      MIXPANEL_SCREENS.DASHBOARD,
      {
        source_day: sourceDay,
        target_days: targetDays,
        target_count: targetDays.length,
      },
    );
    setDraft((prev) =>
      availability.copyDayRangesTo(prev, sourceDay, targetDays),
    );
  };

  const handleClose = () => {
    if (dirty) {
      setDiscardPromptOpen(true);
      return;
    }
    onOpenChange(false);
  };

  const handleConfirmDiscard = () => {
    setDiscardPromptOpen(false);
    setDraft(persisted);
    onOpenChange(false);
  };

  const handleSave = () => {
    mixpanelUtils.trackButtonClick(
      "Save screen availability",
      MIXPANEL_SCREENS.DASHBOARD,
    );
    update.mutate(
      { screeningAvailability: draft },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      },
    );
  };

  const handleOpenChange = (next: boolean) => {
    if (next) {
      onOpenChange(true);
      return;
    }
    handleClose();
  };

  const renderDayRow = (day: DayOfWeek) => {
    const ranges = draft[day];
    const hasRanges = ranges.length > 0;
    const capacity = availability.capacityForDay(ranges);

    return (
      <Row key={day} className="items-start gap-3">
        <Column className="mt-0.5 h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-200">
          <Text className="text-xs font-semibold text-slate-700">
            {day.charAt(0)}
          </Text>
        </Column>
        <Column className="min-w-0 flex-1 gap-2">
          {hasRanges ? (
            <>
              {ranges.map((range, idx) => {
                const isLast = idx === ranges.length - 1;
                return (
                  <Row key={idx} className="items-center gap-2">
                    <TimePicker
                      value={range.startTime}
                      onChange={(v) => {
                        updateRangeField(day, idx, "startTime", v);
                      }}
                    />
                    <Text className="text-sm text-slate-400">–</Text>
                    <TimePicker
                      value={range.endTime}
                      onChange={(v) => {
                        updateRangeField(day, idx, "endTime", v);
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        removeRange(day, idx);
                      }}
                      className="h-8 w-8 shrink-0 text-slate-400 hover:bg-transparent hover:text-slate-700"
                    >
                      <XIcon className="h-3.5 w-3.5" />
                    </Button>
                    {isLast ? (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            addRange(day);
                          }}
                          className="h-8 w-8 shrink-0 text-slate-500 hover:bg-transparent hover:text-slate-900"
                        >
                          <PlusIcon className="h-3.5 w-3.5" />
                        </Button>
                        <CopyDayRangesPopover
                          sourceDay={day}
                          onApply={(targets) => {
                            copyRangesTo(day, targets);
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <Column className="h-8 w-8 shrink-0" />
                        <Column className="h-8 w-8 shrink-0" />
                      </>
                    )}
                  </Row>
                );
              })}
              {capacity > 0 ? (
                <Text className="text-xs leading-snug text-slate-500">
                  Fits up to{" "}
                  <span className="font-medium tabular-nums text-slate-700">
                    {capacity}
                  </span>{" "}
                  candidate {capacity === 1 ? "screen" : "screens"} this day.
                </Text>
              ) : null}
            </>
          ) : (
            <Row className="h-9 items-center gap-2">
              <Text className="flex-1 text-sm text-slate-400">Unavailable</Text>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  addRange(day);
                }}
                className="h-8 w-8 shrink-0 text-slate-500 hover:bg-transparent hover:text-slate-900"
              >
                <PlusIcon className="h-3.5 w-3.5" />
              </Button>
            </Row>
          )}
        </Column>
      </Row>
    );
  };

  const render = () => (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          side="right"
          stopWheelPropagation
          className="flex w-full flex-col gap-0 p-0 sm:max-w-md"
        >
          <SheetHeader className="space-y-1 border-b border-slate-200 px-6 pb-4 pt-6 text-left">
            <SheetTitle>Screen availability</SheetTitle>
            <SheetDescription>
              The scheduling agent will book 15-minute screens inside these
              windows, with 5-minute breaks between, respecting your Google
              Calendar.
              <br /> <br />
              Screening calls will start at minutes :00, :20, or :40.
            </SheetDescription>
          </SheetHeader>

          <Column className="min-h-0 flex-1 gap-5 overflow-y-auto px-6 py-5">
            <Column className="gap-1">
              <Row className="items-center gap-2">
                <RepeatIcon className="h-4 w-4 text-slate-700" />
                <Text className="text-sm font-semibold text-slate-900">
                  Weekly hours
                </Text>
              </Row>
              <Text className="text-xs text-slate-500">
                Set when you are typically available for screens.
              </Text>
            </Column>

            <Column className="gap-3">
              {DAYS_OF_WEEK_ORDERED.map((day) => renderDayRow(day))}
            </Column>
          </Column>

          <Row className="shrink-0 items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-3">
            <Button
              variant="outlineSecondary"
              onClick={handleClose}
              disabled={update.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={update.isPending}>
              Save
            </Button>
          </Row>
        </SheetContent>
      </Sheet>

      <ConfirmationDialog
        open={discardPromptOpen}
        onOpenChange={setDiscardPromptOpen}
        title="Discard changes?"
        description="You have unsaved changes to your screen availability. Closing now will lose them."
        confirmButtonText="Discard"
        cancelButtonText="Keep editing"
        confirmationButtonVariant="destructive"
        onConfirm={handleConfirmDiscard}
      />
    </>
  );

  return render();
};
